import { isEmpty } from 'lodash';
// Import Types
import type { SegmentPreviewClient, SegmentPreviewClientColumn, CurrencyRates } from 'types';
// Import Constants
import { ApplicationColumnIds, ClientKPIColumnTypes } from 'constants/common';
// Import Enums
import { FormatNumberActionEnum } from 'enums';
// Import Helpers
import { Helpers } from 'services';

const { formatNumber, hasLabel } = Helpers;

type CurrencyRateMapping = NonNullable<CurrencyRates[string]>;

/**
 * Determines whether the preview data contains at least one currency column.
 */
export const hasCurrencyColumn = (columns: SegmentPreviewClientColumn[]): boolean =>
    columns.some((column) => hasLabel(column.Type, ClientKPIColumnTypes.Currency));

/**
 * Converts every currency column value in the preview data to the given user currency
 * using the row-level KPICurrency column as the source currency.
 *
 * Data model matches `mapToUIModel` in `pages/ClientViewPage/service.tsx`, but here rows
 * are not grouped by wallet — each row is a standalone client.
 */
export const convertRowsToUserCurrency = (
    clientsData: SegmentPreviewClient,
    userCurrencyRates: Nilable<CurrencyRateMapping>,
): string[][] => {
    const { Columns: columns, Data: rows } = clientsData;

    const currencyColumnIndex = columns.findIndex(
        (column) => column.ApplicationColumnId === ApplicationColumnIds.KPICurrency,
    );

    if (currencyColumnIndex === -1 || isEmpty(userCurrencyRates)) {
        return rows;
    }

    return rows.map((row) => {
        const rowCurrency = row[currencyColumnIndex];

        if (isEmpty(rowCurrency)) {
            return row;
        }

        const rate = userCurrencyRates?.[rowCurrency]?.Rate;

        if (!rate) {
            return row;
        }

        return row.map((value, index) => {
            const isCurrencyColumn = hasLabel(columns[index].Type, ClientKPIColumnTypes.Currency);

            if (!isCurrencyColumn || isEmpty(value)) {
                return value;
            }

            return String(+value * rate);
        });
    });
};

/**
 * Builds the aggregated Total row for the preview table.
 * - First column: "Total of X" (X = row count).
 * - Second column: user currency code.
 * - Currency columns: sum of all row values (formatted with user currency).
 * - Other columns: empty.
 */
export const buildTotalRow = (
    columns: SegmentPreviewClientColumn[],
    convertedRows: string[][],
    userCurrency: string,
    totalLabel: string,
): Record<string, string> => {
    const totalRow: Record<string, string> = {};

    columns.forEach((column, index) => {
        const { DisplayName } = column;

        if (index === 0) {
            totalRow[DisplayName] = totalLabel;
            return;
        }

        if (index === 1) {
            totalRow[DisplayName] = userCurrency;
            return;
        }

        const isCurrencyColumn = hasLabel(column.Type, ClientKPIColumnTypes.Currency);

        if (!isCurrencyColumn) {
            totalRow[DisplayName] = '';
            return;
        }

        const hasAnyValue = convertedRows.some((row) => !isEmpty(row[index]));

        if (!hasAnyValue) {
            totalRow[DisplayName] = '';
            return;
        }

        const sum = convertedRows.reduce((acc, row) => acc + (isEmpty(row[index]) ? 0 : +row[index]), 0);

        totalRow[DisplayName] = `${formatNumber(sum, {
            type: FormatNumberActionEnum.DecimalPrecision,
            precision: 2,
        })} ${userCurrency}`;
    });

    return totalRow;
};
